import { useMemo } from 'react';
import * as Sentry from '@sentry/react';

const { NODE_ENV, VERSION, SENTRY_DSN } = process.env;
const exclude = ['test-runner.js', 'mode-html.js'];
let isInitialized = false;

const init = (productionHostname) => {
  Sentry.init({
    beforeSend(event) {
      const { filename } = event.exception?.values[0]?.stacktrace?.frames[0] || {};
      if (filename && exclude.some((excluded) => filename.endsWith(excluded))) {
        return null;
      }
      return event;
    },
    environment: (
      NODE_ENV !== 'production'
        ? 'development'
        : window.location.hostname === productionHostname
          ? 'production'
          : 'staging'
    ),
    release: VERSION,
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/us-central1-outpost-(272720|staging-344823)\.cloudfunctions\.net\/core-api\//,
        ],
      }),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.2, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.01, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
};

//
// `withSentry`
//
// Llama al método Sentry.init para inicializar Sentry y devuelve el componente
// App envuelto en el HOC Sentry.withProfiler.
//
// Argumentos:
//
// * `productionHostname`: Se usa para determinar el entorno de la aplicación.
// * `App`: Componente de la aplicación.
//
// Variables de entorno que afectan a esta función:
//
// * `NODE_ENV`: Entorno de la aplicación.
// * `VERSION`: Versión de la aplicación.
// * `SENTRY_DSN`: Identificador único que te proporciona Sentry.
//
export const withSentry = (productionHostname, App) => {
  if (!SENTRY_DSN) {
    return App;
  }

  return useMemo(
    () => {
      if (!isInitialized) {
        init(productionHostname);
        isInitialized = true;
      }

      return Sentry.withProfiler(App);
    },
    [App, productionHostname],
  );
};
